(() => {
  if (customElements.get('ss-cart-personalization')) return;
  const same = (a, b) => JSON.stringify(Object.entries(a || {}).sort()) === JSON.stringify(Object.entries(b || {}).sort());
  class CartPersonalization extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.config = JSON.parse(this.querySelector('[data-ss-cart-editor-config]').textContent);
      this.text = this.config.text;
      this.cartSection = this.closest('.shopify-section');
      this.rootUrl = this.dataset.root.replace(/\/?$/, '/');
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency });
      this.dialog = this.querySelector('template').content.firstElementChild.cloneNode(true);
      document.body.append(this.dialog);
      this.form = this.dialog.querySelector('form'); this.form.id = 'ss-cart-editor-form';
      this.scope = this.form.parentElement;
      this.status = this.dialog.querySelector('[data-ss-editor-status]');
      this.error = this.dialog.querySelector('[data-ss-editor-error]');
      this.saveButton = this.dialog.querySelector('[data-ss-editor-save]');
      this.priceLabel = this.dialog.querySelector('[data-ss-editor-price]');
      this.saveLabel = this.saveButton.textContent;
      this.dialog.querySelectorAll('[data-ss-editor-close]').forEach(button => button.addEventListener('click', () => this.close()));
      this.dialog.addEventListener('cancel', event => { if (this.busy) event.preventDefault(); });
      this.dialog.addEventListener('close', () => {
        this.openId = (this.openId || 0) + 1;
        document.documentElement.classList.remove('ss-cart-editor-open');
        this.form.replaceChildren();
        this.trigger?.focus({ preventScroll: true });
      });
      this.form.addEventListener('submit', event => { event.preventDefault(); this.save(); });
      this.scope.addEventListener('ss:personalization:price', event => this.setTotal(event.detail.total));
      this.form.addEventListener('change', () => this.calculate());
      this.form.addEventListener('input', () => this.calculate());
      this.dialog.addEventListener('click', event => this.previewImage(event), true);
      this.observer = new MutationObserver(() => this.addButtons());
      this.observer.observe(this.cartSection, { childList: true, subtree: true });
      this.addButtons();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      if (!this.busy) { this.dialog?.close(); this.dialog?.remove(); }
    }
    addButtons() {
      const products = new Map(this.config.products.map(product => [String(product.id), product]));
      this.cartSection.querySelectorAll('cart-items .cart-item').forEach(row => {
        if (row.querySelector('[data-ss-cart-edit]') || row.classList.contains('cart-item--nested')) return;
        const product = products.get(row.dataset.productId); if (!product) return;
        const button = document.createElement('button'); button.type = 'button'; button.className = 'ss-cart-edit link';
        button.dataset.ssCartEdit = ''; button.textContent = this.text.edit;
        button.addEventListener('click', () => this.open(row, product, button));
        row.querySelector('.cart-item__details').append(button);
      });
    }
    async request(url, body) {
      const response = await fetch(url, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        signal: AbortSignal.timeout(20000),
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {})
      });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : this.text.saveError);
      return data;
    }
    cart() { return this.request(this.rootUrl + 'cart.js'); }
    async productData(url, params = {}) {
      const target = new URL(url, location.origin); target.search = '';
      target.searchParams.set('section_id', 'ss-cart-personalization');
      Object.entries(params).forEach(([key, value]) => target.searchParams.set(key, value));
      const response = await fetch(target, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(this.text.loadError);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const data = JSON.parse(html.querySelector('[data-ss-cart-product-data]')?.textContent || 'null');
      if (!data?.enabled) throw new Error(this.text.loadError);
      return { data, fields: html.querySelector('template[data-ss-cart-fields]') };
    }
    showError(message) { this.error.textContent = message; this.error.hidden = !message; }
    async open(row, product, trigger) {
      if (this.dialog.open || this.busy) return;
      const openId = this.openId = (this.openId || 0) + 1;
      this.trigger = trigger; this.product = product; this.total = null; this.variant = null;
      this.needsReview = false;
      this.prices = new Map(); this.restoring = true; this.form.inert = false;
      this.scope.hidden = true;
      this.form.replaceChildren(); this.priceLabel.textContent = ''; this.showError('');
      this.status.textContent = this.text.loading; this.status.hidden = false; this.saveButton.disabled = true;
      this.dialog.querySelector('[data-ss-editor-product]').textContent = row.querySelector('.js-item-name').textContent.trim();
      this.dialog.showModal(); document.documentElement.classList.add('ss-cart-editor-open');
      try {
        const cart = await this.cart();
        const index = Number(row.id.replace('cart-item-', '')) - 1;
        const line = cart.items[index];
        if (!line || String(line.product_id) !== row.dataset.productId || String(line.variant_id) !== row.dataset.variantId) throw new Error(this.text.changed);
        this.original = line;
        const result = await this.productData(product.url, { variant: line.variant_id });
        if (this.openId !== openId || !this.dialog.open) return;
        if (String(result.data.id) !== String(line.product_id) || !result.fields) throw new Error(this.text.loadError);
        this.data = result.data; this.baseOptions = result.data.variant.options;
        this.form.append(result.fields.content.cloneNode(true));
        window.ss_product_personalization_v2_init?.();
        this.controller = this.form.querySelector('[data-ss-personalization-v2]')?._ssPersonalization;
        if (this.form.querySelector('[data-ss-personalization-v2]') && !this.controller) throw new Error(this.text.loadError);
        if (this.controller?.loading && !(await this.controller.loading)) throw new Error(this.controller.text.stockError);
        if (this.openId !== openId || !this.dialog.open) return;
        this.restore(line.properties || {});
        this.calculate(); this.restoring = false;
        await this.resolvePrice(this.total ?? 0);
        if (this.openId !== openId || !this.dialog.open) return;
        this.status.textContent = this.missing ? this.text.unavailable : ''; this.status.hidden = !this.missing;
        this.setTotal(this.total ?? 0, true);
        this.scope.hidden = false;
      } catch (error) {
        if (this.openId !== openId || !this.dialog.open) return;
        this.form.replaceChildren();
        this.status.hidden = true; this.showError(error.message || this.text.loadError);
      }
    }
    restore(properties) {
      this.managed = new Set(['_personalization_schema', '_personalization_total']); this.missing = false;
      const original = properties;
      if (this.controller) {
        const questions = [...this.controller.schema.questions, ...this.controller.schema.followup_questions];
        questions.forEach(q => {
          [q.title, q.title + ' ' + this.controller.text.size].forEach(title => {
            this.managed.add(title);
            Object.keys(original).filter(key => key.startsWith(title + ' (') && /\(\d+\)$/.test(key)).forEach(key => this.managed.add(key));
          });
        });
        const restore = instance => {
          const q = instance.question;
          const answer = original['_ss_answer_' + instance.path] ?? original[q.title] ?? '';
          if (q.values) {
            const value = q.values.find(v => String(v.id) === String(answer) || v.title === answer);
            if (instance.controls) {
              instance.controls.forEach(input => { input.checked = !!value && !input.disabled && input.value === String(value.id); });
            } else {
              const option = value && [...instance.control.options].find(option => option.value === String(value.id));
              instance.control.value = option && !option.disabled ? option.value : '';
            }
            if (answer && !this.controller.value(instance)) this.missing = true;
            this.controller.updateChildren(instance);
            if (instance.size) {
              const savedSize = original['_ss_size_' + instance.path];
              const savedTitle = original[q.title + ' ' + this.controller.text.size];
              const option = [...instance.size.options].find(option => savedSize ? option.value === String(savedSize) : option.textContent === savedTitle);
              instance.size.value = option && !option.disabled ? option.value : '';
              if ((savedSize || savedTitle) && !instance.size.value) this.missing = true;
            }
            instance.children.forEach(restore);
          } else instance.control.value = answer;
        };
        this.controller.schema.questions.forEach(q => restore(this.controller.instances.get(q.id)));
        this.controller.sync();
      }
      this.form.querySelectorAll('[name^="properties["]').forEach(input => {
        if (input.closest('[data-ss-personalization-v2]') || input.disabled) return;
        const name = input.name.slice(11, -1); this.managed.add(name);
        const value = original[name] || '';
        if (input.type === 'radio' || input.type === 'checkbox') input.checked = input.value === value;
        else if (input.tagName === 'SELECT') {
          input.value = [...input.options].some(option => option.value === value) ? value : '';
          if (value && !input.value) this.missing = true;
        } else input.value = value;
      });
      this.form.querySelectorAll('.ss-product-personalization-popup-gallery').forEach(gallery => {
        gallery.querySelectorAll('a').forEach(link => link.setAttribute('data-no-instant', ''));
      });
    }
    calculate() {
      this.form.querySelectorAll('.ss-product-personalization-popup-gallery').forEach(gallery => {
        if (gallery.parentElement.matches('details')) return;
        const details = document.createElement('details'); details.className = 'ss-cart-editor__images';
        const summary = document.createElement('summary'); summary.textContent = this.text.images;
        gallery.before(details); details.append(summary, gallery);
      });
      window.ss_product_personalization_calculate(jQuery(this.scope));
    }
    setTotal(total, force = false) {
      if (total === this.total && !force) return;
      this.total = total;
      if (this.restoring) return;
      this.saveButton.disabled = true; this.variant = null;
      const openId = this.openId;
      this.resolvePrice(total).then(variant => {
        if (this.openId !== openId || this.total !== total || !this.dialog.open) return;
        this.variant = variant; this.priceLabel.textContent = this.money.format(variant.price / 100) + ' ' + this.text.each;
        this.saveButton.disabled = this.busy || !variant.available;
        this.showError(variant.available ? '' : this.text.unavailable);
      }).catch(error => { if (this.openId === openId && this.total === total) this.showError(error.message); });
    }
    resolvePrice(total) {
      if (this.prices.has(total)) return this.prices.get(total);
      const promise = (async () => {
        const expected = [...this.baseOptions];
        const priceIndex = this.data.options.findIndex(option => option.name === 'Personalization Total');
        if (priceIndex < 0) {
          if (total !== 0) throw new Error(this.text.priceError);
          return this.data.variant;
        }
        const priceOption = this.data.options[priceIndex];
        const priceValue = priceOption.values.find(value => Math.round(Number(value) * 100) === total);
        if (priceValue == null) throw new Error(this.text.priceError);
        expected[priceIndex] = priceValue;
        const ids = this.data.options.map((option, index) => option.ids[option.values.indexOf(expected[index])]);
        if (ids.some(id => !id)) throw new Error(this.text.priceError);
        const { data } = await this.productData(this.product.url, { option_values: ids.join(',') });
        if (String(data.id) !== String(this.original.product_id) || !same(data.variant.options, expected)) throw new Error(this.text.priceError);
        return data.variant;
      })();
      this.prices.set(total, promise); promise.catch(() => this.prices.delete(total));
      return promise;
    }
    close() { if (!this.busy) this.dialog.close(); }
    matches(line, original) {
      return line && line.quantity === original.quantity && line.variant_id === original.variant_id && same(line.properties, original.properties);
    }
    async replaceLine(original, variant, properties) {
      const nonce = crypto.randomUUID(); properties._ss_cart_edit = nonce;
      if (original.variant_id === variant.id) {
        try {
          const cart = await this.request(this.rootUrl + 'cart/change.js', { id: original.key, quantity: original.quantity, properties });
          if (!cart.items.some(line => line.properties?._ss_cart_edit === nonce && line.quantity === original.quantity && same(line.properties, properties))) {
            this.needsReview = true; throw new Error(this.text.review);
          }
          return cart;
        }
        catch (error) {
          let cart;
          try { cart = await this.cart(); }
          catch (_) { this.needsReview = true; throw new Error(this.text.review); }
          if (cart.items.some(line => line.properties?._ss_cart_edit === nonce && line.quantity === original.quantity && same(line.properties, properties))) return cart;
          if (!cart.items.some(line => line.key === original.key)) { this.needsReview = true; throw new Error(this.text.review); }
          throw error;
        }
      }
      const addedLine = { id: variant.id, quantity: original.quantity, properties };
      if (original.selling_plan_allocation) addedLine.selling_plan = original.selling_plan_allocation.selling_plan.id;
      let cause;
      try { await this.request(this.rootUrl + 'cart/add.js', { items: [addedLine] }); } catch (error) { cause = error; }
      let cart;
      try { cart = await this.cart(); }
      catch (_) { this.needsReview = true; throw new Error(this.text.review); }
      let replacement = cart.items.find(line => line.properties?._ss_cart_edit === nonce);
      let current = cart.items.find(line => line.key === original.key);
      if (replacement?.quantity === original.quantity && this.matches(current, original)) {
        try { return await this.request(this.rootUrl + 'cart/change.js', { id: original.key, quantity: 0 }); }
        catch (error) {
          cause = error;
          try { cart = await this.cart(); }
          catch (_) { this.needsReview = true; throw new Error(this.text.review); }
          replacement = cart.items.find(line => line.properties?._ss_cart_edit === nonce);
          current = cart.items.find(line => line.key === original.key);
          if (!current && replacement?.quantity === original.quantity) return cart;
        }
      }
      if (!current) { this.needsReview = true; throw new Error(this.text.review); }
      if (replacement) {
        try { await this.request(this.rootUrl + 'cart/change.js', { id: replacement.key, quantity: 0 }); }
        catch (_) { this.needsReview = true; throw new Error(this.text.review); }
      }
      throw cause || new Error(this.text.changed);
    }
    async save() {
      if (this.busy || this.needsReview) return;
      this.busy = true; this.saveButton.disabled = true; this.saveButton.textContent = this.text.saving; this.showError('');
      this.dialog.querySelectorAll('[data-ss-editor-close]').forEach(button => { button.disabled = true; });
      let reloading = false;
      try {
        if (this.controller && !(await this.controller.beforeSubmit())) return;
        this.calculate();
        if (!this.form.reportValidity()) return;
        this.form.inert = true;
        const variant = await this.resolvePrice(this.total);
        if (!variant.available) throw new Error(this.text.unavailable);
        const cart = await this.cart();
        const original = cart.items.find(line => line.key === this.original.key);
        if (!this.matches(original, this.original)) throw new Error(this.text.changed);
        const properties = Object.fromEntries(Object.entries(original.properties || {}).filter(([key]) => !this.managed.has(key) && !key.startsWith('_ss_answer_') && !key.startsWith('_ss_size_')));
        for (const [name, value] of new FormData(this.form)) {
          if (name.startsWith('properties[') && value !== '') properties[name.slice(11, -1)] = value;
        }
        await this.replaceLine(original, variant, properties);
        location.reload();
        reloading = true;
      } catch (error) { this.showError(error.message || this.text.saveError); }
      finally {
        if (!reloading) {
          this.busy = false; this.form.inert = false; this.saveButton.textContent = this.saveLabel;
          this.saveButton.disabled = this.needsReview || !this.variant?.available;
          this.dialog.querySelectorAll('[data-ss-editor-close]').forEach(button => { button.disabled = false; });
        }
      }
    }
    previewImage(event) {
      const target = event.target.closest('.ss-product-personalization-popup-gallery a, img.ss_lightbox');
      if (!target) return;
      event.preventDefault(); event.stopImmediatePropagation();
      const preview = document.createElement('dialog'); preview.className = 'ss-cart-editor__preview';
      const close = document.createElement('button'); close.type = 'button'; close.className = 'ss-cart-editor__close';
      close.setAttribute('aria-label', this.dialog.querySelector('[data-ss-editor-close]').getAttribute('aria-label')); close.textContent = '×';
      const image = document.createElement('img'); image.src = target.href || target.src; image.alt = target.title || target.alt || '';
      preview.append(close, image); this.dialog.append(preview); close.addEventListener('click', () => preview.close());
      preview.addEventListener('close', () => preview.remove()); preview.showModal();
    }
  }
  customElements.define('ss-cart-personalization', CartPersonalization);
})();